// src/analyticsDimensions/types.ts
var analyticsDimensionsKeys = [
  "lb_active_screen_name",
  "lb_active_screen_type",
  "lb_process",
  "lb_path",
  "lb_client_session",
  "lb_hashed_userid",
  "lb_version",
  "lb_client",
  "lb_locale",
  "lb_environment",
  "lb_os",
  "lb_age",
  "lb_gender",
  "lb_country",
  "lb_color_mode",
  "lb_company_name"
];
function isAnalyticsDimensions(value) {
  const keys = analyticsDimensionsKeys;
  return keys.includes(value);
}

// src/analyticsDimensions/utils/buildAnalyticsDimensions.ts
import { v4 as uuid } from "uuid";
function buildAnalyticsDimensions() {
  const dimensions = {};
  dimensions.lb_client_session = uuid();
  return dimensions;
}

// src/analyticsDimensions/utils/getAnalyticsDimensions.ts
function getAnalyticsDimensions() {
  return window.analyticsState.analyticsDimensions;
}

// src/analyticsDimensions/utils/updateAnalyticsDimensions.ts
function updateAnalyticsDimensions(analyticsDimensions) {
  const dimensions = getAnalyticsDimensions();
  Object.entries(analyticsDimensions).forEach(([key, value]) => {
    if (isAnalyticsDimensions(key)) {
      if (value !== void 0) {
        dimensions[key] = value;
      } else {
        delete dimensions[key];
      }
    }
  });
}

// src/analyticsDimensions/utils/tokenToDimensions.ts
function tokenToDimensions(token) {
  const dimensions = {
    ...getAnalyticsDimensions(),
    lb_locale: token.locale?.split("-")[0] ?? "",
    lb_gender: token.gender,
    lb_age: token.age,
    lb_user_type: token.user_type,
    lb_company_name: token.user_type === "corporate" ? token["customer.actingName"] : void 0
    // We are only allowed to log company name not personal names
  };
  updateAnalyticsDimensions(dimensions);
}

// src/analyticsDimensions/utils/setDefaultDimensions.ts
function getOperatingSystem() {
  if (typeof window === "undefined") return "";
  const userAgent = window.navigator.userAgent;
  if (userAgent.includes("Windows")) return "Windows";
  if (userAgent.includes("Mac")) return "macOS";
  if (userAgent.includes("Linux")) return "Linux";
  if (userAgent.includes("Android")) return "Android";
  if (userAgent.includes("iPhone") || userAgent.includes("iPad") || userAgent.includes("iPod"))
    return "iOS";
  return "";
}
function setDefaultDimensions(info) {
  const dimensions = {
    ...getAnalyticsDimensions(),
    ...info,
    lb_user_theme: "light",
    // use light as default while theming is not implemented
    lb_os: getOperatingSystem()
  };
  updateAnalyticsDimensions(dimensions);
}

// src/utils/analyticTime.ts
var padNum = (num) => String(num).padStart(2, "0");
function timestampsForEvent() {
  const now = new Date(Date.now());
  return {
    lb_timestamp: now.getTime(),
    lb_naive_time: `${now.getFullYear()}-${padNum(now.getMonth() + 1)}-${padNum(
      now.getDate()
    )} ${padNum(now.getHours())}:${padNum(now.getMinutes())}:${padNum(now.getSeconds())}.${padNum(
      now.getMilliseconds()
    )}`
  };
}

// src/backends/backendManager.ts
var buildBackendManager = (backends) => {
  return {
    isActive() {
      return backends.some((backend) => backend.isActive());
    },
    logEvent(name, params) {
      const timeForEvent = timestampsForEvent();
      backends.forEach(
        (backend) => backend.logEvent(name, { ...params, ...timeForEvent })
      );
    },
    logScreen(name, type) {
      backends.forEach((backend) => backend.logScreen(name, type));
    },
    setUser(username) {
      backends.forEach((backend) => backend.setUser(username));
    },
    showInstrumentation() {
      return backends.map((backend) => backend.showInstrumentation?.()).filter((result) => result !== void 0).join("\n");
    }
  };
};

// src/backends/googleBackend/utils/events.ts
var logEvent = ({
  eventName,
  source,
  label,
  element,
  ...params
}) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, {
      source,
      label,
      element,
      ...params
    });
  }
};
var logClick = (params) => {
  logEvent({ eventName: "lb_click", ...params });
};
var setUser = (params) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("set", "user_properties", params);
  }
};

// src/backends/googleBackend/utils/pageView.ts
var pageView = ({
  pageTitle,
  pageLocation,
  pagePath,
  params
}) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
      ...params
    });
  }
};

// src/backends/googleBackend/utils/screenView.ts
var screenView = ({ screenName, params }) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "lb_screen_view", {
      lb_screen_name: screenName,
      lb_screen_type: "screen",
      ...params
    });
  }
};

// src/backends/googleBackend/googleBackend.ts
var buildGoogleBackend = () => {
  let isActive = false;
  return {
    isActive() {
      return isActive;
    },
    logEvent(name, params) {
      logEvent({
        eventName: name,
        ...params,
        ...getAnalyticsDimensions(),
        ...timestampsForEvent2()
      });
    },
    logScreen(name, type) {
      screenView({
        screenName: name,
        params: {
          type
        },
        ...getAnalyticsDimensions(),
        ...timestampsForEvent2()
      });
      pageView({
        pageTitle: name,
        pageLocation: window.location.href,
        ...getAnalyticsDimensions()
      });
    },
    setUser(username) {
      isActive = !!username;
      setUser({
        user_id: username ?? ""
      });
    },
    showInstrumentation() {
      return "Google analytics backend does not support instrumentation";
    }
  };
};
function timestampsForEvent2() {
  const now = new Date(Date.now());
  return {
    lb_timestamp: now.getTime(),
    lb_naive_time: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
      now.getDate()
    ).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}.${String(
      now.getMilliseconds()
    ).padStart(2, "0")}`
  };
}

// src/backends/mockBackend.ts
function consoleLog(prefix, value, params, storedDimensions) {
  console.log(
    `Analytics: ${prefix}` + (value !== void 0 ? `=${value}` : ""),
    params,
    storedDimensions || {
      ...getAnalyticsDimensions()
    }
  );
}
var trailData = [];
function silentLog(prefix, value, params) {
  trailData.push({
    prefix,
    value,
    params: { ...params },
    dimensions: { ...getAnalyticsDimensions() }
  });
}
var mockEventHandler = (_mockRoute) => {
  return {
    logEvent(_name, _params) {
    }
  };
};
var buildMockAnalyticsBackend = (mockRoute) => {
  const handler = mockEventHandler(mockRoute);
  const log = silentLog;
  return {
    isActive() {
      return true;
    },
    logEvent(name, params) {
      handler.logEvent(name, {
        ...params,
        ...getAnalyticsDimensions()
      });
      log("Event", name, params);
    },
    logScreen(name, type) {
      handler.logEvent("lb_screen_view", {
        lb_screen_name: name,
        lb_screen_type: type,
        ...getAnalyticsDimensions()
      });
      log("lb_screen_view", name);
    },
    setUser(username) {
      handler.logEvent("set_userproperties", {
        user_id: username ?? ""
      });
      log("setUser", username);
    },
    showInstrumentation() {
      trailData.forEach((trail) => {
        consoleLog(trail.prefix, trail.value, trail.params, trail.dimensions);
      });
      trailData = [];
      return "Started showing instrumentation successfully";
    }
  };
};

// src/backends/posthogBackend.ts
import posthog from "posthog-js";

// src/utils/parseBoolean.ts
var parseBoolean = (str) => {
  if (!str) {
    return false;
  }
  try {
    return Boolean(JSON.parse(str));
  } catch {
    return false;
  }
};

// src/backends/posthogBackend.ts
var buildPosthogBackend = () => {
  let isActive = false;
  let hog;
  const apiKey = typeof window !== "undefined" ? window.__INITIAL_STATE__?.POSTHOG_API_KEY : process.env.POSTHOG_API_KEY;
  const apiHost = typeof window !== "undefined" ? window.__INITIAL_STATE__?.POSTHOG_API_HOST : process.env.POSTHOG_API_HOST;
  const isMockAnalytics = parseBoolean(
    typeof window !== "undefined" ? window.__INITIAL_STATE__?.LOG_MOCK_ANALYTICS : process.env.LOG_MOCK_ANALYTICS
  );
  if (apiKey && apiHost && typeof window !== "undefined") {
    hog = posthog;
    window.posthog = isMockAnalytics ? posthog : void 0;
    hog.init(apiKey, {
      api_host: apiHost,
      // person_profiles: 'identified_only', TODO: decide if we want to enable this
      // opt_out_capturing_by_default: true, TODO: decide if we want to enable this
      persistence: "sessionStorage",
      before_send(event) {
        const properties = event?.properties;
        if (properties?.["$current_url"]) {
          const url = properties["$current_url"];
          const updatedUrl = url?.split("?")[0];
          properties["$current_url"] = updatedUrl;
        }
        return event;
      }
    });
  }
  return {
    isActive() {
      return isActive;
    },
    logEvent(name, params) {
      const dimensions = getAnalyticsDimensions();
      hog?.capture(name, {
        $current_url: dimensions.lb_active_screen_name ?? dimensions.lb_path,
        ...params,
        ...dimensions
      });
    },
    logScreen(name, type) {
      const dimensions = getAnalyticsDimensions();
      hog?.capture("lb_screen_view", {
        $current_url: name,
        ...dimensions,
        lb_screen_name: name,
        lb_screen_type: type
      });
      hog?.capture("$screen", {
        $current_url: name,
        $screen: name,
        $screen_name: name,
        ...dimensions,
        lb_screen_name: name,
        lb_screen_type: type
      });
    },
    // NOTE: This function is not currently used in production.
    // Its purpose is to enable/disable analytics based on whether the user is logged in.
    // Will be reviewed and properly implemented in January 2026 after Christmas.
    setUser(username) {
      isActive = !!username;
      hog?.reset();
      if (isActive) {
        hog?.opt_in_capturing();
      } else {
        hog?.opt_out_capturing();
      }
      if (username) {
        hog?.identify(username);
      } else {
        hog?.reset();
      }
    },
    showInstrumentation() {
      return "PostHog analytics backend does not support instrumentation";
    }
  };
};

// src/meteringService.ts
function getAnalyticsManager() {
  if (typeof window !== "undefined" && typeof window.analyticsState === "object") {
    return window.analyticsState.analyticsManager;
  }
}
var buildMeteringService = () => {
  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __tag: "desktopMeteringService",
    notifyClick: (args) => {
      getAnalyticsManager()?.logClick(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src
      });
    },
    notifySelect: (args) => {
      const data = {
        ...marshallData(args.data),
        lb_src_component: args.src
      };
      if (args.selected) {
        data["lb_value"] = args.selected;
      }
      getAnalyticsManager()?.logSelect(args.instrumentAs, data);
    },
    notifyEffect: (args) => {
      getAnalyticsManager()?.logEffect(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src
      });
    },
    notifyView: (args) => {
      const data = {
        ...marshallData(args.data),
        lb_src_component: args.src
      };
      if (args.action) {
        data["lb_action"] = args.action;
      }
      getAnalyticsManager()?.logView(args.instrumentAs, data);
    },
    notifyStartProcess: (args) => {
      getAnalyticsManager()?.logStartProcess(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src
      });
    },
    notifyEndProcess: (args) => {
      getAnalyticsManager()?.logEndProcess(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src
      });
    },
    notifyShowView: (args) => {
      getAnalyticsManager()?.logScreen(args.instrumentAs, "dialog");
    },
    notifyAddToWallet: (_) => {
    },
    notifySubmit: (args) => {
      getAnalyticsManager()?.logSubmit(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src
      });
    },
    notifyNavigate: (args) => {
      getAnalyticsManager()?.logNavigate(args.instrumentAs, args.to, {
        ...marshallData(args.data),
        lb_src_component: args.src
      });
    },
    notifyInteract: (args) => {
      getAnalyticsManager()?.logInteract(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src
      });
    },
    notifyKeyPress: (args) => {
      getAnalyticsManager()?.logKeyPress(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src,
        lb_key: args.key
      });
    },
    notifyFocus: (args) => {
      getAnalyticsManager()?.logFocus(args.instrumentAs, {
        ...marshallData(args.data),
        lb_src_component: args.src,
        lb_action: args.action
      });
    }
  };
};
function marshallData(data) {
  if (data === void 0 || data === null) {
    return {};
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    return data;
  }
  return { lb_data: data };
}
function getMeteringService() {
  if (typeof window !== "undefined" && typeof window.analyticsState.meteringService === "object") {
    return window.analyticsState.meteringService;
  }
}

// src/AnalyticsManager.ts
function getAnalyticsManager2() {
  if (typeof window !== "undefined" && typeof window.analyticsState === "object") {
    return window.analyticsState.analyticsManager;
  }
}
function marshallData2(data) {
  if (data === void 0 || data === null) {
    return {};
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    return data;
  }
  return { lb_data: data };
}
function prefixAnalyticParams(params) {
  const prefixedAnalyticParams = {};
  Object.keys(params).forEach((key) => {
    if (key.startsWith("lb_")) {
      prefixedAnalyticParams[key] = params[key];
    } else {
      prefixedAnalyticParams[`lb_${key}`] = params[key];
    }
  });
  return prefixedAnalyticParams;
}
var buildAnalyticsManager = () => {
  const initialState = typeof window !== "undefined" ? window.__INITIAL_STATE__ : void 0;
  const backend = process.env.NODE_ENV === "production" && !parseBoolean(initialState?.LOG_MOCK_ANALYTICS) ? buildBackendManager([buildGoogleBackend(), buildPosthogBackend()]) : buildMockAnalyticsBackend("/api/captureLog");
  const processStack = [];
  let lastScreen;
  let prevUsername;
  return {
    setUser: function(username) {
      console.log("AnalyticsManager setUser", { username, prevUsername });
      if (username && username !== prevUsername) {
        backend.setUser(username);
        prevUsername = username;
      }
    },
    clearUser: function() {
      backend.setUser(void 0);
      prevUsername = void 0;
    },
    logEvent: function(name, params) {
      if (!name.startsWith("lb_")) {
        name = `lb_${name}`;
      }
      params = prefixAnalyticParams(params ?? {});
      backend.logEvent(name, params);
    },
    logClick: function(source, params) {
      backend.logEvent("lb_click", {
        ...params,
        lb_source: source
      });
    },
    logSelect: function(source, params) {
      backend.logEvent("lb_select", {
        ...params,
        lb_source: source
      });
    },
    logView: function(source, params) {
      backend.logEvent("lb_view", { ...params, lb_source: source });
    },
    logAuth: function(params) {
      backend.logEvent("lb_authorisation", params);
    },
    logEffect: function(name, params) {
      backend.logEvent("lb_effect", { ...params, lb_source: name });
    },
    logScreen: function(name, type) {
      if (type === "screen" && name === lastScreen) {
        return;
      }
      backend.logScreen(name, type);
      updateAnalyticsDimensions({
        lb_active_screen_name: name,
        lb_active_screen_type: type
      });
      if (type === "screen") {
        lastScreen = name;
      }
    },
    logStartProcess: function(name, params) {
      backend.logEvent("lb_start_process", { ...params, lb_source: name });
      processStack.push(name);
      const dimensions = getAnalyticsDimensions();
      if (dimensions.lb_active_screen_name && dimensions.lb_active_screen_type) {
        updateAnalyticsDimensions({
          lb_process: processStack.join(">")
        });
      }
    },
    logEndProcess: function(name, params) {
      backend.logEvent("lb_end_process", {
        ...params,
        lb_source: name
      });
      if (processStack.length > 0) {
        processStack.pop();
      } else {
        return;
      }
      if (lastScreen) {
        backend.logScreen(lastScreen, "screen");
        updateAnalyticsDimensions({
          lb_process: processStack.length > 0 ? processStack.join(">") : void 0,
          lb_active_screen_name: lastScreen,
          lb_active_screen_type: "screen"
        });
      }
    },
    logSubmit: function(source, params) {
      backend.logEvent("lb_submit", {
        ...params,
        lb_source: source
      });
    },
    logNavigate: function(source, to, params) {
      this.logEvent("lb_navigate", {
        ...params,
        lb_to: to,
        lb_source: source
      });
    },
    logInteract: function(source, params) {
      this.logEvent("lb_interaction", {
        ...params,
        lb_source: source
      });
    },
    logKeyPress: function(source, params) {
      this.logEvent("lb_key_press", {
        ...params,
        lb_source: source
      });
    },
    logFocus: function(source, params) {
      this.logEvent("lb_focus", {
        ...params,
        lb_source: source
      });
    },
    showInstrumentation: function() {
      return backend.showInstrumentation?.() ?? "Failed to show instrumentation";
    }
  };
};
if (typeof window !== "undefined" && window.analyticsState === void 0) {
  const manager = buildAnalyticsManager();
  window.analyticsState = {
    analyticsManager: manager,
    analyticsDimensions: buildAnalyticsDimensions(),
    meteringService: buildMeteringService(),
    showInstrumentation: manager.showInstrumentation
  };
}

// src/consent.ts
var GOOGLE_CONSENT = {
  analytics_storage: "granted",
  ad_storage: "denied",
  functionality_storage: "denied",
  personalization_storage: "denied",
  ad_personalization: "denied",
  security_storage: "denied",
  ad_user_data: "denied"
};
function analyticsConsent({ consentType }) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("consent", consentType, GOOGLE_CONSENT);
  }
}

// src/useAnalytics.ts
function useAnalytics() {
  return {
    logEvent: (event, params) => {
      getAnalyticsManager2?.()?.logEvent(event, params || {});
    },
    logClick: (click, params) => {
      const clickParams = { ...params };
      getAnalyticsManager2?.()?.logClick(click, clickParams);
    },
    logImpression: (params) => {
      getAnalyticsManager2?.()?.logEvent("lb_impression", params);
    },
    logAuth: (params) => {
      getAnalyticsManager2?.()?.logAuth(params);
    },
    logShowScreen: (instrumentAs, type = "screen") => {
      if (instrumentAs === "ignore" || instrumentAs === "loading") {
        return;
      }
      if (screenStack.length >= 1) {
        const last = screenStack[screenStack.length - 1];
        if (last.name === instrumentAs) {
          return;
        }
      }
      screenStack.push({ name: instrumentAs, type });
      getAnalyticsManager2?.()?.logScreen(instrumentAs, type);
      return () => {
        const item = screenStack.pop();
        if (item != void 0 && screenStack.length > 0) {
          const last = screenStack[screenStack.length - 1];
          getAnalyticsManager2?.()?.logScreen(last.name, last.type);
        }
      };
    },
    updateDimensions: (newDimensions) => {
      updateAnalyticsDimensions(newDimensions);
    }
  };
}
var screenStack = Array();

// src/ViewMeter.tsx
import { useContainerMedia } from "@landsbankinn/ui-kit";
import { useEffect } from "react";
function ViewMeter({ location }) {
  const { updateDimensions } = useAnalytics();
  const isTablet = useContainerMedia("lg", "smaller or equal");
  const isMobile = useContainerMedia("md", "smaller or equal");
  useEffect(() => {
    updateDimensions({ lb_path: location });
  }, [location, updateDimensions]);
  useEffect(() => {
    let renderDimension;
    if (isMobile) {
      renderDimension = "phone";
    } else if (isTablet) {
      renderDimension = "tablet";
    } else {
      renderDimension = "desktop";
    }
    updateDimensions({
      lb_render_dimension: renderDimension
    });
  }, [isTablet, isMobile, updateDimensions]);
  return null;
}

// src/useProcessSuccessAnalytics.ts
import { useLocalStorage } from "@landsbankinn/utils";
var PROCESS_SESSION_STORAGE_KEY = "ANALYTICS/PROCESS_SUCESS";
var initialValue = {
  "account created": { type: "unset" },
  "card created": { type: "unset" },
  "loan created": { type: "unset" },
  "contact information confirmed": { type: "unset" }
};
function useProcessSuccessAnalytics() {
  const analytics = useAnalytics();
  const [state, setState] = useLocalStorage(
    PROCESS_SESSION_STORAGE_KEY,
    initialValue
  );
  return {
    addOnSuccessLogEvent(process2, params) {
      setState({ ...state, [process2]: { type: "log event", params } });
    },
    onSuccess(process2) {
      const processInstructions = state[process2];
      if (processInstructions.type === "log event") {
        const [name, params] = processInstructions.params;
        analytics.logEvent(name, params);
        setState(initialValue);
      }
    },
    onDropOut(process2) {
      const processInstructions = state[process2];
      if (processInstructions.type === "log event") {
        const [name, params] = processInstructions.params;
        analytics.logEvent("quit_" + name, params);
        setState(initialValue);
      }
    }
  };
}
export {
  GOOGLE_CONSENT,
  ViewMeter,
  analyticsConsent,
  analyticsDimensionsKeys,
  buildAnalyticsDimensions,
  buildMeteringService,
  getAnalyticsDimensions,
  getAnalyticsManager2 as getAnalyticsManager,
  getMeteringService,
  isAnalyticsDimensions,
  logClick,
  logEvent,
  marshallData2 as marshallData,
  pageView,
  screenView,
  setDefaultDimensions,
  setUser,
  tokenToDimensions,
  updateAnalyticsDimensions,
  useAnalytics,
  useProcessSuccessAnalytics
};
//# sourceMappingURL=index.mjs.map